import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Users, CheckCircle, Sparkles, Scale, Award, Heart, Lightbulb } from 'lucide-react';
import InquiryModal from '../components/InquiryModal';

const About = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');

  const handleButtonClick = (context) => {
    setButtonContext(context);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section - Market Oriented */}
      <section className="relative bg-gradient-to-br from-[#012169] via-[#013d9e] to-[#012169] py-24 overflow-hidden">
        {/* Modern Abstract Background */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1620641788421-7a1c342ea42e" 
            alt="Abstract background"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#F97316] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#8B5CF6] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-block mb-6">
            <span className="text-[#F97316] text-sm font-bold uppercase tracking-widest px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border-2 border-[#F97316]/50">
              Why Choose Us
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight tracking-tight">
            Protecting Brands Since
            <br />
            <span className="text-[#F97316] drop-shadow-[0_4px_20px_rgba(249,115,22,0.8)]">2008 with Excellence</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed font-light">
            We're more than a trademark registration service—we're your intellectual property protection partner. With 15+ years of experience, 10,000+ successful filings, and a 98% USPTO approval rate, we deliver results that secure your competitive advantage.
          </p>
        </div>
      </section>

      {/* Results-Driven Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F97316] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#012169] opacity-5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <p className="text-base font-bold text-[#F97316] uppercase tracking-widest mb-4">Why Choose Us</p>
            <h2 className="text-5xl md:text-6xl font-black text-[#012169] mb-6 tracking-tight">Trademark Registration Experts</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Your brand deserves professional protection. We combine legal expertise, transparent pricing, and personalized service to deliver trademark registration that safeguards your business growth and market position.
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto mb-16">
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-10 md:p-12 border-2 border-[#F97316]/20 hover:border-[#F97316] transition-all duration-500">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <h3 className="text-3xl font-black text-[#012169] mb-4">What Makes Us Different</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-[#10B981] flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-gray-900">98% Success Rate</p>
                        <p className="text-gray-600 text-sm">Industry-leading approval rate on all filings</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-[#10B981] flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-gray-900">24-48 Hour Filing</p>
                        <p className="text-gray-600 text-sm">Fast-track your protection without delays</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-[#10B981] flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-gray-900">Licensed Attorneys</p>
                        <p className="text-gray-600 text-sm">Every application reviewed by experienced IP lawyers</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <h3 className="text-3xl font-black text-[#012169] mb-4">Real Business Impact</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our clients don't just get legal protection—they gain competitive advantages, investor confidence, and the freedom to scale without brand threats. We've helped thousands of businesses secure their market position and grow with confidence.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    From startups to established enterprises, our streamlined process and expert guidance ensure your intellectual property becomes a strategic business asset, not just paperwork.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: Shield, 
                title: 'Risk-Free Guarantee', 
                desc: 'Money-back guarantee if we can\'t secure your trademark', 
                color: 'from-[#012169] to-[#F97316]' 
              },
              { 
                icon: Sparkles, 
                title: 'Strategic Advantage', 
                desc: 'Position your brand for market dominance and investor appeal', 
                color: 'from-[#FFD700] to-[#FFA500]' 
              },
              { 
                icon: Users, 
                title: 'Dedicated Support', 
                desc: 'Direct access to your attorney throughout the entire process', 
                color: 'from-blue-500 to-cyan-500' 
              },
              { 
                icon: Award, 
                title: 'Proven Track Record', 
                desc: '10,000+ businesses protected with 98% approval success', 
                color: 'from-green-500 to-emerald-500' 
              }
            ].map((item, index) => (
              <Card key={index} className="group p-8 text-center hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 hover:border-[#F97316] hover:-translate-y-3 bg-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/0 to-[#FFD700]/0 group-hover:from-[#FFD700]/5 group-hover:to-[#F97316]/5 transition-all duration-500"></div>
                <div className={`w-20 h-20 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-xl`}>
                  <item.icon className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-black text-[#012169] mb-3 group-hover:text-[#F97316] transition-colors relative z-10">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed relative z-10">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Benefits Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-base font-bold text-[#FFD700] uppercase tracking-widest mb-4">Business Benefits</p>
            <h2 className="text-5xl md:text-6xl font-black text-[#012169] mb-6 tracking-tight">How We Protect Your Bottom Line</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Smart IP protection isn't just legal compliance—it's a strategic business decision that drives revenue, reduces risk, and builds enterprise value.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { 
                icon: Shield, 
                title: 'Market Protection', 
                desc: 'Prevent competitors from copying your brand name, logo, or products. Exclusive rights mean exclusive market position—giving you the legal power to defend your territory and customer base.',
                gradient: 'from-[#012169] to-[#F97316]',
                metric: 'Avoid $50K+ in legal disputes'
              },
              { 
                icon: Award, 
                title: 'Investor Confidence', 
                desc: 'Protected IP dramatically increases business valuation. Investors and acquirers pay premium multiples for businesses with secured trademarks and patents—it\'s proof of defensible market advantage.',
                gradient: 'from-[#FFD700] to-[#FFA500]',
                metric: 'Up to 3x higher valuation'
              },
              { 
                icon: Sparkles, 
                title: 'Revenue Expansion', 
                desc: 'Trademarks unlock licensing opportunities, Amazon Brand Registry benefits, and premium positioning. Protected brands command higher prices and open doors to exclusive distribution channels.',
                gradient: 'from-blue-500 to-cyan-500',
                metric: 'Unlock new revenue streams'
              },
              { 
                icon: Lightbulb, 
                title: 'Risk Mitigation', 
                desc: 'Avoid costly rebranding, litigation, and marketplace bans. Filing early prevents trademark conflicts that could cost hundreds of thousands in legal fees and brand rebuilding.',
                gradient: 'from-green-500 to-emerald-500',
                metric: 'Eliminate expensive risks'
              }
            ].map((value, index) => (
              <Card key={index} className="group relative overflow-hidden p-10 hover:shadow-2xl transition-all duration-500 border-2 border-gray-200 hover:border-[#F97316] bg-white">
                {/* Animated Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${value.gradient} rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg flex-shrink-0`}>
                      <value.icon className="h-8 w-8 text-white" />
                    </div>
                    <span className="text-sm font-bold text-[#F97316] bg-[#FFD700]/20 px-4 py-2 rounded-full">{value.metric}</span>
                  </div>
                  <h3 className="text-2xl font-black text-[#012169] mb-4 group-hover:text-[#F97316] transition-colors duration-300">{value.title}</h3>
                  <div className="h-1 w-16 bg-gradient-to-r from-[#FFD700] to-[#F97316] rounded-full mb-4 group-hover:w-full transition-all duration-500"></div>
                  <p className="text-gray-700 leading-relaxed text-lg">{value.desc}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#FFD700] opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-[#F97316] opacity-10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <p className="text-base font-bold text-[#FFD700] uppercase tracking-widest mb-4">Expert Guidance</p>
            <h2 className="text-5xl md:text-6xl font-black text-[#012169] mb-6 tracking-tight">Attorneys Who Understand Business</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our specialized legal team doesn't just file paperwork—they craft protection strategies that align with your business goals and market positioning.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { 
                role: 'Trademark Strategy', 
                title: 'Brand Protection Experts',
                desc: 'Navigate complex trademark searches, overcome USPTO objections, and secure registrations for clients in competitive markets. 500+ successful filings annually.',
                bgGradient: 'from-[#012169] to-[#F97316]',
                icon: Shield
              },
              { 
                role: 'Business IP Strategy', 
                title: 'Portfolio Architects',
                desc: 'Build comprehensive IP portfolios aligned with your growth strategy. We protect not just your current brand, but your future expansion plans.',
                bgGradient: 'from-[#FFD700] to-[#FFA500]',
                icon: Lightbulb
              },
              { 
                role: 'Enforcement & Defense', 
                title: 'Market Protection',
                desc: 'Proactive monitoring and enforcement to stop infringers. We don\'t just file—we defend your rights and eliminate marketplace threats.',
                bgGradient: 'from-blue-500 to-cyan-500',
                icon: Scale
              }
            ].map((member, index) => (
              <Card key={index} className="group relative p-8 hover:shadow-2xl transition-all duration-500 border-2 border-gray-200 hover:border-[#F97316] hover:-translate-y-3 bg-white overflow-hidden">
                {/* Hover Background Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/0 to-[#F97316]/0 group-hover:from-[#FFD700]/5 group-hover:to-[#F97316]/5 transition-all duration-500"></div>
                
                <div className="relative z-10">
                  {/* Icon with Animated Border */}
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-br ${member.bgGradient} rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                      <member.icon className="h-10 w-10 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-black text-[#012169] mb-3 group-hover:text-[#F97316] transition-colors duration-300">{member.title}</h3>
                  <div className="h-1 w-16 bg-gradient-to-r from-[#FFD700] to-[#F97316] rounded-full mb-4 group-hover:w-full transition-all duration-500"></div>
                  <p className="text-[#F97316] font-bold mb-3 text-sm uppercase tracking-wider">{member.role}</p>
                  <p className="text-gray-600 leading-relaxed">{member.desc}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#012169] via-[#8B1587] to-[#6A0572]">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#FFD700] opacity-20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F97316] opacity-20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500 opacity-10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10 shadow-2xl">
            <div className="text-center">
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#FFD700] to-[#F97316] rounded-2xl mb-6 shadow-lg">
                <Shield className="h-10 w-10 text-white" />
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-[#F1F1F1] mb-6">Ready to Protect Your Brand?</h2>
              <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                Let our expert team help you secure your intellectual property rights.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  onClick={() => handleButtonClick('Start Trademark Registration - About CTA')}
                  className="group relative bg-[#FFD700] hover:bg-[#e5a200] text-[#012169] font-semibold text-lg px-10 py-7 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 overflow-hidden"
                >
                  <span className="relative z-10">Start Trademark Registration</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700] to-[#F97316] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => handleButtonClick('Discuss with Attorney - About CTA')}
                  className="group relative border-2 border-[#AB2346] text-[#F1F1F1] hover:bg-[#F97316] hover:text-white font-semibold text-lg px-10 py-7 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#AB2346]/50"
                >
                  <span className="relative z-10">Discuss with Attorney</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <InquiryModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        buttonContext={buttonContext}
      />
    </div>
  );
};

export default About;